<script lang="ts" setup>
import { OcticonButton, SancareDatePicker, SancareModal, SancareOcticon } from '@sancare/typescript-commons'
import dayjs, { Dayjs } from 'dayjs'
import { computed, onMounted, Ref, ref, watch } from 'vue'

import CompletionFilter from '@/filters/CompletionFilter.vue'
import FiltersData from '@/filters/FiltersData'
import { useStore } from '@/store'
import { CompletionFilterItem } from '@/store/modules/filters/types'
import { BaseAct, BaseCodedAct, lateralityOptions, ToBeAddedAct } from '@/store/modules/medical-act/types'
import { CountryEnum, PmsiTypeEnum } from '@/store/modules/settings/types'
import { BaseSummaryUnit } from '@/store/modules/stay/types'

const store = useStore()
const emit = defineEmits(['close-form'])
const props = withDefaults(defineProps<{
  preSelectedMedicalAct?: { reference: string, description: string } | null
  summaryUnit: BaseSummaryUnit<Dayjs, BaseAct, BaseCodedAct<Dayjs, BaseAct>>
  pmsiCountry?: CountryEnum
  pmsiType: PmsiTypeEnum
  presenceDays?: string
  visible: boolean
}>(), {
  preSelectedMedicalAct: null,
  pmsiCountry: CountryEnum.FR,
  presenceDays: '1111111',
})

const completionVisible = ref(false)
const newCodedActMsg = ref('')
const newCodedActMsgContext = ref('')
const newCodedActDescription = ref('')

const actFilterData = computed(() => Object.assign(FiltersData.medicalAct, { subFilters: [] }))
const completionItems = computed(() => store.state.remoteLists.completionItems)
const isAddingCodedAct = computed(() => store.state.medicalAct.requests.addMedicalAct.fetching)
const newActExecutionDate = computed(() => dayjs(newCodedAct.value.executionDate))
const absenceDays = computed(() => {
  const absenceDays = []
  let currentDay = dayjs(props.summaryUnit.startDate)
  for (const presence of props.presenceDays) {
    if (presence === '0') {
      absenceDays.push(currentDay.clone())
    }
    currentDay = currentDay.add(1, 'd')
  }
  return absenceDays
})
const firstPresenceDay = computed(() => {
  let currentDay = dayjs(props.summaryUnit.startDate)
  for (const presence of props.presenceDays) {
    if (presence === '1') {
      return currentDay
    }
    currentDay = currentDay.add(1, 'd')
  }
  return null
})
const newCodedAct = ref(getEmptyNewCodedAct()) as Ref<ToBeAddedAct>

async function addAct() {
  if (isAddingCodedAct.value) {
    return
  }
  if (props.pmsiCountry === CountryEnum.FR) {
    if (newCodedAct.value.reference === '') {
      newCodedActMsg.value = 'Aucune référence sélectionnée'
      newCodedActMsgContext.value = 'danger'
      return
    }
    if (!newCodedAct.value.activity) {
      newCodedActMsg.value = 'Aucune activité sélectionnée'
      newCodedActMsgContext.value = 'danger'
      return
    }
  } else {
    if (!newCodedAct.value.laterality && newCodedAct.value.laterality !== '') {
      newCodedActMsg.value = 'Aucune latéralité sélectionnée'
      newCodedActMsgContext.value = 'danger'
      return
    }
  }
  newCodedActMsg.value = ''
  const startMut = {
    name: props.pmsiType === PmsiTypeEnum.SMR ? 'smrStay/startRefreshingGroupingResult' : null
  }
  const successMut = {
    name: props.pmsiType === PmsiTypeEnum.SMR ? 'smrStay/updateMedicalActsSuccess' : 'updateMedicalActsSuccess',
  }
  await store.dispatch('medicalAct/addMedicalAct', {
    body: {
      ...newCodedAct.value,
    },
    urlParams: {
      pmsiType: props.pmsiType,
      summaryUnitId: props.summaryUnit.id,
    },
    startMut,
    successMut,
  })
  if (store.state.medicalAct.requests.addMedicalAct.ok) {
    resetNewCodedAct()
    closeModal()
    return
  }
  newCodedActMsg.value = store.state.medicalAct.requests.addMedicalAct.error.message
  newCodedActMsgContext.value = 'danger'
}
async function chooseAct(item: CompletionFilterItem) {
  if (props.pmsiCountry === CountryEnum.CH && !completionItems.value.some((completionItem) => completionItem.reference === item.value)) {
    // completionItems can be stale (debounced fetch not resolved yet when the user
    // typed a reference and clicked "Ok" right away): re-check against the exact
    // reference before rejecting it.
    await store.dispatch('remoteLists/getCompletionItems', {
      url: '/api/stay-search-criteria/completion',
      urlParams: {
        type: actFilterData.value.completionName,
        search: item.value,
        contextDate: dayjs(props.summaryUnit.endDate).format('YYYY-MM-DD'),
      },
      signal: new AbortController().signal,
    })
    if (!completionItems.value.some((completionItem) => completionItem.reference === item.value)) {
      newCodedActMsg.value = 'Cet acte est inconnu'
      newCodedActMsgContext.value = 'danger'
      return
    }
  }
  newCodedActMsg.value = ''
  newCodedAct.value.reference = item.value
  newCodedActDescription.value = item.description
  completionVisible.value = false
}
function getEmptyNewCodedAct(): ToBeAddedAct {
  return {
    reference: '',
    executionDate: dayjs(firstPresenceDay.value).format('YYYY-MM-DD'),
    justifications: null,
  }
}
function resetNewCodedAct() {
  completionVisible.value = true
  newCodedAct.value = getEmptyNewCodedAct()
}
function setExecutionDate(executionDate: Dayjs) {
  newCodedAct.value.executionDate = executionDate.format('YYYY-MM-DD')
}

function closeModal() {
  emit('close-form')
  newCodedActMsg.value = ''
}

function preSelectMedicalAct() {
  newCodedAct.value.reference = props.preSelectedMedicalAct.reference
  newCodedActDescription.value = props.preSelectedMedicalAct.description
  completionVisible.value = false
}

watch(() => props.preSelectedMedicalAct, () => {
  if (props.preSelectedMedicalAct) {
    preSelectMedicalAct()
  } else {
    newCodedAct.value.reference = ''
    newCodedActDescription.value = ''
    completionVisible.value = true
  }
})

onMounted(() => {
  resetNewCodedAct()
  if (props.preSelectedMedicalAct) {
    preSelectMedicalAct()
  }
})
</script>

<template>
  <sancare-modal
    :open="visible"
    @close-modal="closeModal()"
  >
    <template #header>
      <div class="row">
        <h3 class="col-auto me-auto mb-0">
          {{ $t('medicalAct.add') }}
        </h3>
        <octicon-button
          class="col-auto"
          style="font-size: 1rem;"
          octicon-name="x"
          @click="closeModal()"
        />
      </div>
    </template>
    <template #default>
      <div class="row">
        <label class="col-2 col-form-label text-end fw-bold">Date&nbsp;:</label>
        <div class="col">
          <sancare-date-picker
            :model-value="newActExecutionDate"
            :min-date="dayjs(summaryUnit.startDate)"
            :max-date="dayjs(summaryUnit.endDate)"
            :disabled-dates="absenceDays"
            @update:model-value="setExecutionDate"
          />
        </div>
      </div>
      <div class="row">
        <label class="col-2 col-form-label text-end fw-bold">Référence&nbsp;:</label>
        <div class="col">
          <completion-filter
            v-if="completionVisible"
            class="mb-2"
            :pmsi-type="pmsiType"
            :filter-data="actFilterData"
            :context-date="dayjs(summaryUnit.endDate)"
            @add-filter="chooseAct"
          />
          <div
            v-else
            class="col-form-label"
          >
            <strong>{{ newCodedAct.reference }}</strong>
            <span class="ms-2 cp">
              <sancare-octicon
                name="pencil"
                :width="15"
                :height="15"
                @click="completionVisible = true"
              />
            </span>
            <p class="mb-0">
              {{ newCodedActDescription }}
            </p>
          </div>
        </div>
      </div>
      <div
        v-if="pmsiCountry === CountryEnum.FR"
        class="row"
      >
        <label class="col-2 col-form-label text-end fw-bold">Activité&nbsp;:</label>
        <div class="col">
          <input
            v-model="newCodedAct.activity"
            type="number"
            min="1"
            max="4"
          >
        </div>
      </div>
      <div
        v-else
        class="row"
      >
        <label class="col-2 col-form-label text-end fw-bold">Latéralité&nbsp;:</label>
        <div class="col">
          <select
            v-model="newCodedAct.laterality"
            class="form-select col-5"
          >
            <option
              v-for="laterality in lateralityOptions"
              :key="laterality.value"
              :value="laterality.value"
            >
              {{ laterality.label }}
            </option>
          </select>
        </div>
      </div>
      <div class="row mt-3">
        <div class="col-auto ms-auto">
          <button
            class="btn btn-primary"
            @click="addAct"
          >
            <span v-if="!isAddingCodedAct">Enregistrer</span>
            <span
              v-else
              class="loader loader-xs mx-4"
            />
          </button>
        </div>
        <div
          v-if="newCodedActMsg !== ''"
          class="col-12"
        >
          <span :class="'text-'+newCodedActMsgContext">{{ newCodedActMsg }}</span>
        </div>
      </div>
    </template>
  </sancare-modal>
</template>
